import { Signal } from 'typed-signals';
import { v4 } from 'uuid';
import { isConnectedBlocker } from 'game/boardMVC/utils/isConnectedBlocker';
import debounce from 'lodash/debounce';
import cloneDeep from 'lodash/cloneDeep';
import {
  type BlockerExportState,
  type BlockerModel,
  type BoardScheduler,
  type BoardState,
  type HiddenStack,
  type TileStackState,
  ConnectionStatus,
} from '../../types';
import { TileStackModel } from './TileStackModel';
import { TileModel } from './TileModel';
import { EventEmitter } from './BoardEventEmitter';
import { BoardEventType, TileRemovalReason } from './BoardEvents';
import type {
  BlockerChangedEvent,
  BlockerRemovedEvent,
  CollectableEvent,
  MoveMadeEvent,
  StacksSwappedEvent,
  TileMovedEvent,
  TileRemovedFinishedEvent,
  TileRemovedEvent,
} from './BoardEvents';
import { MatchSequencerMVC } from './MatchSequencerMCV';
import { BOARD_SETTINGS } from '../../../scene/GameSceneConstants';
import { NoViewScheduler } from './NoViewScheduler';
import { BlockersFactory } from '../blockers/BlockersFactory';
import { KnotBlockerModel } from '../blockers/KnotBlockerModel';
import type { LevelTaskProgress, LevelTaskType } from '../../../tasks/types';
import type { TileColor } from '../../TileColor';
import { TasksRunner } from '../../../../libs/TasksRunner';

export interface BoardModelSettings {
  state: BoardState;
  scheduler?: BoardScheduler;
}

type StackGrid = (TileStackModel | null)[][];

export class BoardModel {
  public readonly beforeMakeMoveClean = new Signal();

  public readonly events = new EventEmitter<{
    [BoardEventType.TileMoved]: TileMovedEvent;
    [BoardEventType.TilesRemoved]: TileRemovedEvent;
    [BoardEventType.TileRemovedFinished]: TileRemovedFinishedEvent;
    [BoardEventType.BlockerRemoved]: BlockerRemovedEvent;
    [BoardEventType.BlockerChanged]: BlockerChangedEvent;
    [BoardEventType.CollectableRelease]: CollectableEvent;
    [BoardEventType.StacksSwapped]: StacksSwappedEvent;
    [BoardEventType.MoveMade]: MoveMadeEvent;
  }>();

  private readonly tasksRunner = new TasksRunner();

  private readonly blockersFactory = new BlockersFactory(this, this.tasksRunner);

  private readonly scheduler: BoardScheduler;

  private readonly initialState: BoardState;

  private readonly matchSequencer: MatchSequencerMVC;

  private readonly grid: StackGrid;

  public readonly cols: number;

  public readonly rows: number;

  private readonly stacks = new Map<string, TileStackModel>();

  public readonly onStateUpdate = new Signal<() => void>();

  constructor({ state, scheduler }: BoardModelSettings) {
    this.scheduler = scheduler ?? new NoViewScheduler();
    this.initialState = state;
    this.cols = state.cols;
    this.rows = state.rows;
    this.grid = this.createStackGrid(state);
    this.fillStacks(state.stacks);

    this.initBlockers(state.blockers ?? []);

    this.matchSequencer = new MatchSequencerMVC({
      board: this,
      taskExecutor: this.tasksRunner,
      onPathResolved: (stack: TileStackModel): void => {
        this.tasksRunner.addTask(this.checkTilesForMatchRemoval(stack));
      },
      scheduler: this.scheduler,
    });
  }

  public importState(state: BoardState): void {
    this.stacks.forEach((stack) => {
      stack.reset();
    });
    this.blockersFactory.reset();
    this.fillStacks(state.stacks);
    this.initBlockers(state.blockers ?? []);
  }

  public initBlockers(blockers: BlockerExportState[]): void {
    (blockers ?? []).forEach((blockerState) => {
      const stack = this.getStack(blockerState.x, blockerState.y);
      if (!stack) return;
      this.blockersFactory.addBlockerToStack(stack, blockerState);
    });

    this.blockersFactory.onRemovalRequest.connect((stack, blocker) => {
      this.removeBlocker(stack, blocker);
    });

    this.blockersFactory.onBlockerChanged.connect((stack, blocker) => {
      this.events.emit(BoardEventType.BlockerChanged, {
        id: `blockerChange_${blocker.id}_${v4()}`,
        stackId: stack.id,
        blockerId: blocker.id,
      });
    });

    this.blockersFactory.onReleaseCollectable.connect((stack) => {
      this.events.emit(BoardEventType.CollectableRelease, { stack });
    });

    this.blockersFactory.onPostLevelInitSetup();
  }

  public onScoreUpdated(score: number): void {
    this.blockersFactory.onScoreChanged(score);
  }

  public onLevelBlockerTaskUpdate(taskType: LevelTaskType, progress: LevelTaskProgress): void {
    this.blockersFactory.onLevelBlockerTaskUpdate(taskType, progress);
  }

  public onLevelTaskComplete(taskType: LevelTaskType): void {
    this.blockersFactory.onLevelTaskComplete(taskType);
  }

  public removeBlocker(stack: TileStackModel, blocker: BlockerModel): void {
    this.tasksRunner.addTask(this.removeBlockerGenerator(stack, blocker));
  }

  private *removeBlockerGenerator(stack: TileStackModel, blocker: BlockerModel): Generator<void, void> {
    const eventId = `blockerRemoved_${blocker.id}_${v4()}`;

    if (!blocker.underTile) {
      stack.locked = true;
    }

    if (isConnectedBlocker(blocker)) {
      blocker.connection.status = ConnectionStatus.WillBeRemoved;
    }

    yield* this.scheduler.waitGenerator(null, BOARD_SETTINGS.timing.blockerRemovalScheduleDelay);

    const duration = BOARD_SETTINGS.timing.blockerRemovalDuration;

    this.events.emit(BoardEventType.BlockerRemoved, {
      id: eventId,
      stackId: stack.id,
      blocker,
      blockerId: blocker.id,
      time: duration,
    });

    const schedulerGenerator = this.scheduler.waitGenerator(eventId, duration);

    if (blocker instanceof KnotBlockerModel) {
      yield* this.removeConnectedKnotBlockers(stack, blocker);
    }

    yield* schedulerGenerator;

    if (blocker.underTile) {
      stack.setUnderTileBlocker(null);
    } else {
      stack.setBlocker(null);
    }

    if (!blocker.underTile) {
      stack.locked = false;
    }

    this.checkMatchesFromStack(stack.id);
  }

  private createStackGrid({ cols, rows, hiddenStacks }: BoardState): StackGrid {
    const stackGrid: (TileStackModel | null)[][] = [];

    const isStackHidden = (stackX: number, stackY: number): boolean =>
      hiddenStacks.some(({ x, y }) => x === stackX && y === stackY);

    // Create grid TileStacks.
    for (let x = 0; x < cols; x += 1) {
      const colStacks: (TileStackModel | null)[] = [];
      stackGrid.push(colStacks);
      for (let y = 0; y < rows; y += 1) {
        if (!isStackHidden(x, y)) {
          const stack = new TileStackModel(x, y);
          this.stacks.set(stack.id, stack);
          colStacks.push(stack);
        } else {
          colStacks.push(null);
        }
      }
    }

    return stackGrid;
  }

  public createStack(x: number, y: number): TileStackModel {
    const stack = new TileStackModel(x, y);
    this.stacks.set(stack.id, stack);
    this.grid[stack.x][stack.y] = stack;
    this.generateStackConnections();
    return stack;
  }

  public removeStack(stack: TileStackModel): void {
    if (!this.stacks.has(stack.id)) return;
    this.stacks.delete(stack.id);
    this.grid[stack.x][stack.y] = null;
    this.generateStackConnections();
  }

  private fillStacks(stackStates: TileStackState[]): void {
    for (let i = 0; i < stackStates.length; i += 1) {
      const stackState = stackStates[i];
      const stack = this.getStack(stackState.x, stackState.y);
      stack?.addTiles(stackState.tiles.map((color) => new TileModel(color)));
    }

    this.generateStackConnections();
  }

  public getStack(x: number, y: number): TileStackModel | null {
    return this.grid?.[x]?.[y] ?? null;
  }

  public getStackById(id: string): TileStackModel | null {
    return this.stacks.get(id) ?? null;
  }

  public getStacks(): TileStackModel[] {
    return this.grid.flat().filter(Boolean) as TileStackModel[];
  }

  public makeMove(stackId: string, tiles: TileModel[]): void {
    const stack = this.getStackById(stackId);
    if (!stack) return;

    stack.addTiles(tiles);

    this.events.emit(BoardEventType.MoveMade, {
      stackId,
      tiles: tiles.slice(),
      tilesIds: tiles.map(({ id }) => id),
    });

    this.checkMatchesFromStack(stackId);
  }

  public *transferTiles(
    fromStack: TileStackModel,
    toStack: TileStackModel,
    tilesAmount?: number,
  ): Generator<void, void> {
    const eventId = `move_${v4()}`;

    const amountToMove = tilesAmount ?? fromStack.getTopmostColor();
    const tilesToMove = fromStack.removeTilesAmount(amountToMove);

    if (tilesToMove.length === 0) return;
    fromStack.locked = true;

    toStack.addTiles([...tilesToMove].reverse());
    const duration = tilesToMove.length * BOARD_SETTINGS.timing.moveTileDuration;
    this.events.emit(BoardEventType.TileMoved, {
      id: eventId,
      fromStackId: fromStack.id,
      toStackId: toStack.id,
      tileIds: tilesToMove.map(({ id }) => id),
      time: duration,
    });
    yield* this.scheduler.waitGenerator(eventId, duration);
    fromStack.locked = false;
  }

  public checkMatchesFromStack(stackId: string, startDelay?: number): void {
    const stack = this.stacks.get(stackId);
    if (!stack) return;
    this.matchSequencer.checkMatches(stack, startDelay);
    this.broadcastStateUpdate();
  }

  public hasActiveMatches(): boolean {
    return this.matchSequencer.activeMatches > 0;
  }

  public getMatchValuesForStack(stack: TileStackModel): Record<TileColor, number> {
    return this.matchSequencer.getMatchValuesForStack(stack);
  }

  public isIdle(): boolean {
    return !this.tasksRunner.hasAnyRunningTasks();
  }

  public update(deltaTime: number): void {
    this.scheduler.update(deltaTime);
    this.tasksRunner.update();
  }

  private generateStackConnections(): void {
    this.stacks.forEach((stack) => {
      stack.setConnectedStacks(this.getConnectedStacks(stack));
    });
  }

  private getConnectedStacks(stack: TileStackModel): TileStackModel[] {
    const connectedStacks: TileStackModel[] = [];
    const originStack = this.getStack(stack.x, stack.y);
    if (!originStack) return connectedStacks;

    const isEvenCol = stack.x % 2 === 0;

    for (let x = -1; x <= 1; x += 1) {
      for (let y = -1; y <= 1; y += 1) {
        if ((x === 0 && y === 0) || Math.abs(x + y) === 2) continue;

        const stackX = stack.x + x;
        let stackY = stack.y + y;

        if (isEvenCol && stackX < stack.x) stackY -= 1;
        else if (!isEvenCol && stackX > stack.x) stackY += 1;

        const connectedStack = this.getStack(stackX, stackY);
        if (!connectedStack) continue;
        connectedStacks.push(connectedStack);
      }
    }
    return connectedStacks;
  }

  private *checkTilesForMatchRemoval(stack: TileStackModel): Generator<void, void> {
    const { matchLength } = BOARD_SETTINGS;
    const color = stack.getTopmostColor();
    const matchingTileCount = stack.getMatchingColorCount(color);

    if (!stack.isBlocked() && matchingTileCount >= matchLength) {
      yield* this.removeTilesFromStackGenerator({
        stack,
        tilesAmount: matchingTileCount,
        reason: TileRemovalReason.Match,
      });
      this.checkMatchesFromStack(stack.id, BOARD_SETTINGS.timing.matchAfterRemovalDelay);
    } else {
      this.broadcastStateUpdate();
    }
  }

  public removeTilesFromStack(config: { stack: TileStackModel; tilesAmount: number; reason: TileRemovalReason }): void {
    this.tasksRunner.addTask(this.removeTilesFromStackGenerator(config));
  }

  private *removeTilesFromStackGenerator(config: {
    stack: TileStackModel;
    tilesAmount: number;
    reason: TileRemovalReason;
  }): Generator<void, void> {
    const { timing } = BOARD_SETTINGS;
    const { stack, tilesAmount, reason } = config;
    const id = `removal_${stack.id}_${tilesAmount}_${v4()}`;

    if (tilesAmount <= 0) return;

    stack.locked = true;

    const tiles = stack.removeTilesAmount(tilesAmount);
    const lockDuration =
      reason === TileRemovalReason.Booster
        ? timing.quickRemovalDuration(tilesAmount)
        : timing.removalDuration(tilesAmount);

    this.events.emit(BoardEventType.TilesRemoved, {
      id,
      stackId: stack.id,
      tileIds: tiles.map((tile) => tile.id),
      tiles: tiles.slice(),
      time: lockDuration,
      reason,
    });
    yield* this.scheduler.waitGenerator(id, lockDuration);

    stack.locked = false;

    this.events.emit(BoardEventType.TileRemovedFinished, {
      id,
      stackId: stack.id,
    });

    if (reason === TileRemovalReason.Match) {
      this.blockersFactory.onMatch(stack, tiles);
    }

    this.broadcastStateUpdate();
  }

  public hasLockedStacks(): boolean {
    return this.getStacks().some((stack) => stack.locked);
  }

  public getEmptyStacks(): TileStackModel[] {
    return this.getStacks().filter((stack) => stack.isEmpty());
  }

  public getEmptyStackCount(): number {
    return this.getStacks().reduce((a, stack) => a + (stack.isEmpty() ? 1 : 0), 0);
  }

  public hasEmptyStacks(): boolean {
    return this.getStacks().some((stack) => stack.isEmpty());
  }

  public isBoardEmpty(): boolean {
    return this.getStacks().every((stack) => stack.isEmpty());
  }

  public isBoardFull(): boolean {
    return this.matchSequencer.activeMatches === 0 && !this.hasEmptyStacks() && !this.hasLockedStacks();
  }

  public hasMoveableStacks(): boolean {
    return this.getStacks().some((stack) => !stack.isEmpty() && !stack.isBlocked());
  }

  public hasOrphanedStacks(): boolean {
    return this.getStacks().some((stack) => stack.getConnectedStacks().length === 0);
  }

  public broadcastStateUpdate = debounce(() => {
    if (this.matchSequencer.activeMatches > 0 || this.hasLockedStacks()) return;
    this.onStateUpdate.emit();
  }, 200);

  public destroy(): void {
    this.events.destroy();
  }

  public exportState(): BoardState {
    const { cols, rows, initialState } = this;
    const stackStates: TileStackState[] = [];
    const blockerStates: BlockerExportState[] = [];
    const hiddenStacks: HiddenStack[] = [];

    for (let y = 0; y < rows; y += 1) {
      for (let x = 0; x < cols; x += 1) {
        const stack = this.getStack(x, y);
        if (!stack) {
          hiddenStacks.push({ x, y });
          continue;
        }
        if (stack.hasTiles()) stackStates.push(stack.exportState());
        if (stack.blocker)
          blockerStates.push({
            ...stack.blocker.exportState(),
            x: stack.x,
            y: stack.y,
          });

        if (stack.underTileBlocker) {
          blockerStates.push({
            ...stack.underTileBlocker.exportState(),
            x: stack.x,
            y: stack.y,
          });
        }
      }
    }

    return cloneDeep({
      ...initialState,
      stacks: stackStates,
      blockers: blockerStates,
      hiddenStacks,
    });
  }

  public getScheduler(): BoardScheduler {
    return this.scheduler;
  }

  private *removeConnectedKnotBlockers(stack: TileStackModel, blocker: KnotBlockerModel): Generator<void, void> {
    const connectedNeighbors = stack.getConnectedBlockerStacks(true);

    yield* this.scheduler.waitGenerator(null, BOARD_SETTINGS.timing.blockerRemovalScheduleDelay);

    connectedNeighbors.forEach((neighbor) => {
      const { blocker: neighborBlocker } = neighbor;
      if (
        neighborBlocker &&
        neighborBlocker instanceof KnotBlockerModel &&
        neighborBlocker.connection.status === ConnectionStatus.Connected &&
        neighbor !== stack
      ) {
        neighborBlocker.requestRemoval.emit();
      }
    });

    blocker.connection.status = ConnectionStatus.Removed;
  }
}
